import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IInvoicePayment } from "../type/exhibitorType";

interface UpdatePaymentStatusModalProps {
	open: boolean;
	handleClose: () => void;
	payment: IInvoicePayment | null;
	apiEndpoint?: string;
}

const STATUS_OPTIONS = [
	{ value: "approved", label: "Approved" },
	{ value: "pending", label: "Pending" },
	{ value: "rejected", label: "Rejected" },
];

const UpdatePaymentStatusModal: React.FC<UpdatePaymentStatusModalProps> = ({
	open,
	handleClose,
	payment,
	apiEndpoint,
}) => {
	const [status, setStatus] = useState<string>("approved");

	useEffect(() => {
		if (payment?.status) {
			setStatus(payment.status.toLowerCase());
		} else {
			setStatus("approved");
		}
	}, [payment]);

	const endpoint =
		apiEndpoint || `/api/admin/b2b/invoice/payment/${payment?.id || ""}`;

	const { mutate: updateStatus, isPending } = useApiMutation<{ status: string }, any>({
		method: "PATCH",
		endPoint: endpoint,
		invalidateQueries: [
			"b2b-single-MR",
			"b2b_invoice",
			"singleInvoice",
			"visitorSingleInvoice",
			"Exhibitor",
		],
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!payment?.id) return;

		updateStatus(
			{
				status: status,
			},
			{
				onSuccess: () => {
					handleClose();
				},
				onError: (err: any) => {
					console.error("Failed to update payment status:", err);
				},
			}
		);
	};

	if (!payment) return null;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			disableEnforceFocus
			translate="no"
			className="notranslate"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1.5,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<EditTwoToneIcon color="primary" fontSize="small" />
					<Typography variant="h5" fontWeight={600}>
						<span>Update Payment Status</span>
					</Typography>
				</Box>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit}>
				<DialogContent sx={{ pt: 2.5, pb: 2 }}>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: 2 }}
					>
						<span>Payment ID: </span>
						{payment.amount ? <span> (৳{payment.amount})</span> : null}
					</Typography>

					<FormControl fullWidth size="small">
						<InputLabel id="update-payment-status-label">Status</InputLabel>
						<Select
							labelId="update-payment-status-label"
							id="update-payment-status-select"
							value={status}
							label="Status"
							onChange={(e) => setStatus(e.target.value)}
						>
							{STATUS_OPTIONS.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									<span>{opt.label}</span>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>

				<Divider />

				<DialogActions sx={{ p: 2, px: 2.5, gap: 1 }}>
					<Button
						variant="outlined"
						color="inherit"
						onClick={handleClose}
						disabled={isPending}
						size="small"
					>
						<span>Cancel</span>
					</Button>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						disabled={isPending}
						size="small"
						startIcon={
							isPending ? (
								<CircularProgress size={14} color="inherit" />
							) : undefined
						}
					>
						<span>{isPending ? "Updating..." : "Update Status"}</span>
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdatePaymentStatusModal;
